/**
 * The countdown ring.
 *
 * The product's one piece of illustration, and it illustrates the only thing
 * that really distinguishes it: the conversation is on a clock. Drawn as inline
 * SVG like every other mark here — the Content Security Policy allows no remote
 * origin, so there is nothing to load.
 *
 * `fraction` is how much of the period remains, 0–1. It is presentational: the
 * authoritative expiry always comes from the server.
 */
export function Ring({
  value,
  label,
  fraction = 1,
  size = 168,
}: {
  value: string;
  label: string;
  fraction?: number;
  size?: number;
}) {
  const radius = 74;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.min(1, Math.max(0, fraction));

  // 48 marks: one per half hour of the 24-hour life, which is why the dial
  // reads as a clock rather than as decoration.
  const ticks = Array.from({ length: 48 }, (_, index) => {
    const angle = (index / 48) * Math.PI * 2 - Math.PI / 2;
    const inner = radius - 9;
    return {
      x1: 90 + Math.cos(angle) * inner,
      y1: 90 + Math.sin(angle) * inner,
      x2: 90 + Math.cos(angle) * (inner - 5),
      y2: 90 + Math.sin(angle) * (inner - 5),
    };
  });

  return (
    <div className="ring">
      <svg
        width={size}
        height={size}
        viewBox="0 0 180 180"
        role="img"
        aria-label={`${value} ${label}`}
      >
        <circle className="ring__track" cx="90" cy="90" r={radius} />
        <g className="ring__ticks">
          {ticks.map((tick) => (
            <line key={`${tick.x1},${tick.y1}`} {...tick} />
          ))}
        </g>
        <circle
          className="ring__arc"
          stroke="url(#ring-gradient)"
          cx="90"
          cy="90"
          r={radius}
          strokeDasharray={`${circumference * clamped} ${circumference}`}
          transform="rotate(-90 90 90)"
        />
        <text className="ring__value" x="90" y="88" textAnchor="middle" dominantBaseline="middle">
          {value}
        </text>
        <text className="ring__label" x="90" y="112" textAnchor="middle">
          {label}
        </text>
      </svg>
    </div>
  );
}
